import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import Button, { ButtonType } from '../../components/Button/Button';
import { Flex } from '../../components/Flex/Flex';
import { Sizing } from '../../styles/spacing';
import { HoverTooltip } from '../../components/HoverTooltip/HoverTooltip';
import {
  CardsIcon,
  EmptyProjectIcon,
  ListIcon,
  PremiumBadge,
} from '../../components/Icons';
import { Navbar } from '../../components/Navbar/Navbar';
import { Text } from '../../components/Text/Text';
import { FontSize } from '../../styles/typography';
import { useModal } from '../../context/Modal/useModal';
import { useUser } from '../../context/User/useUser';
import { isAdmin } from '../../helpers/user';
import { toRelativeTime } from '../../helpers/relativeTime';
import { SubscriptionModal } from '../Subscription/SubscriptionModal';
import { CopyProjectModal } from './Modals/CopyProjectModal';
import { DeleteProjectModal } from './Modals/DeleteProjectModal';
import { EditProjectModal } from './Modals/EditProjectModal';
import { NewProjectModal } from './Modals/NewProjectModal';
import {
  CompactDrawingActions,
  CompactDrawingBox,
  CompactDrawings,
  DrawingActions,
  DrawingActionsMenu,
  DrawingActionsTrigger,
  DrawingBox,
  Drawings,
  DensityToggle,
  DensityToggleButton,
  NewProjectBox,
  Project,
  PromoBox,
  Title,
} from './Projects.styles';
import { useProjectsDensity } from './useProjectsDensity';

function Projects() {
  const { canvases, isSubscribed } = useUser();
  const modal = useModal();
  const intl = useIntl();
  const { isDense, setIsDense } = useProjectsDensity();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const showAsCardsLabel = intl.formatMessage({ id: 'projects.showAsCards' });
  const showAsListLabel = intl.formatMessage({ id: 'projects.showAsList' });

  const onOpenNewProjectModal = () => {
    modal.onOpen(<NewProjectModal />);
  };

  const onOpenDeleteModal = (canvasId: string) => {
    modal.onOpen(<DeleteProjectModal canvasId={canvasId} />);
  };

  const onOpenEditModal = (canvasId: string) => {
    modal.onOpen(<EditProjectModal canvasId={canvasId} />);
  };

  const onOpenCopyModal = (canvasId: string, canvasName: string) => {
    modal.onOpen(
      <CopyProjectModal canvasId={canvasId} canvasName={canvasName} />
    );
  };

  const onOpenSubscriptionModal = () => {
    modal.onOpen(<SubscriptionModal />);
  };

  const formatUpdatedAt = (updatedAt: string) => {
    const { value, unit } = toRelativeTime(updatedAt);
    return intl.formatRelativeTime(value, unit, { numeric: 'auto' });
  };

  const showPromotion = false;

  const user = useUser();
  const hasReachedMaxProjects =
    !isSubscribed && canvases.length > 0 && !isAdmin(user);

  const ProjectList = isDense ? CompactDrawings : Drawings;
  const ProjectBox = isDense ? CompactDrawingBox : DrawingBox;

  const renderActionButtons = (canvasId: string, name: string) => (
    <>
      <Button
        onClick={() => onOpenEditModal(canvasId)}
        type={ButtonType.PrimaryLink}
      >
        <FormattedMessage id="projects.editButton" />
      </Button>
      <Button
        onClick={() => onOpenCopyModal(canvasId, name)}
        type={ButtonType.PrimaryLink}
        disabled={hasReachedMaxProjects}
      >
        <FormattedMessage id="projects.copyButton" />
      </Button>
      <Button
        onClick={() => onOpenDeleteModal(canvasId)}
        type={ButtonType.DangerLink}
      >
        <FormattedMessage id="projects.deleteButton" />
      </Button>
    </>
  );

  return (
    <Project>
      <Navbar />
      {!isSubscribed && (
        <PromoBox>
          <PremiumBadge width="88" height="88" />
          <Flex $flexDirection="column" $alignItems="start" $gap={Sizing.sm}>
            <Text
              $fontSize={FontSize.lg}
              $fontWeight="bolder"
              $color="var(--text)"
            >
              <FormattedMessage id="projects.promoText" />
            </Text>
            <Text $fontSize={FontSize.md}>
              <FormattedMessage id="projects.promoSubtext" />
            </Text>
            {showPromotion && (
              <Text
                $fontSize={FontSize.sm}
                $fontWeight="bold"
                $color="var(--status-green-text-only)"
              >
                <FormattedMessage id="projects.promoPrice" />
              </Text>
            )}
            <Button
              onClick={() => onOpenSubscriptionModal()}
              type={ButtonType.Primary}
            >
              <FormattedMessage id="projects.upgradeButton" />
            </Button>
          </Flex>
        </PromoBox>
      )}
      <Flex
        $flexDirection="row"
        $alignItems="center"
        $justifyContent="space-between"
      >
        <Title>
          <FormattedMessage id="projects.title" />
        </Title>
        <DensityToggle>
          <DensityToggleButton
            type="button"
            $active={!isDense}
            aria-pressed={!isDense}
            aria-label={showAsCardsLabel}
            title={showAsCardsLabel}
            onClick={() => setIsDense(false)}
          >
            <CardsIcon />
          </DensityToggleButton>
          <DensityToggleButton
            type="button"
            $active={isDense}
            aria-pressed={isDense}
            aria-label={showAsListLabel}
            title={showAsListLabel}
            onClick={() => setIsDense(true)}
          >
            <ListIcon />
          </DensityToggleButton>
        </DensityToggle>
      </Flex>
      <Flex $flexDirection="column" $gap={Sizing.lg}>
        <Flex $flexDirection="column">
          <NewProjectBox>
            <EmptyProjectIcon width="88" height="88" />
            <Flex $flexDirection="column">
              {canvases.length === 0 ? (
                <h2>
                  <FormattedMessage id="projects.newProjectFirstTime" />
                </h2>
              ) : (
                <h2>
                  <FormattedMessage id="projects.newProject" />
                </h2>
              )}
              {hasReachedMaxProjects ? (
                <>
                  <Text $fontSize={FontSize.md}>
                    <FormattedMessage id="projects.subscribeToCreateMore" />
                  </Text>
                  <HoverTooltip
                    text={intl.formatMessage({
                      id: 'projects.subscribeToCreateMore',
                    })}
                  >
                    {' '}
                    <Button
                      onClick={onOpenNewProjectModal}
                      disabled={hasReachedMaxProjects}
                    >
                      <FormattedMessage id="projects.newProjectButton" />
                    </Button>
                  </HoverTooltip>
                </>
              ) : (
                <Button
                  onClick={onOpenNewProjectModal}
                  disabled={hasReachedMaxProjects}
                >
                  <FormattedMessage id="projects.newProjectButton" />
                </Button>
              )}
            </Flex>
          </NewProjectBox>
        </Flex>
        <Flex $flexDirection="column" $gap={Sizing.sm}>
          <ProjectList>
            {canvases.map((c) => (
              <ProjectBox key={c.canvasId}>
                <Link to={`/projects/${c.canvasId}`}>
                  <h2>{c.name}</h2>
                  <p>
                    <span>
                      <FormattedMessage
                        id="projects.scaleAndFormat"
                        values={{
                          scale: c.drawingHeadScale,
                          format: intl.formatMessage({
                            id: `format.${c.pageFormat}`,
                          }),
                        }}
                      />
                    </span>
                    <span>
                      <FormattedMessage
                        id="projects.updatedAt"
                        values={{ date: formatUpdatedAt(c.updatedAt) }}
                      />
                    </span>
                  </p>
                </Link>
                {isDense ? (
                  <CompactDrawingActions
                    tabIndex={-1}
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setOpenMenuId(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setOpenMenuId(null);
                      }
                    }}
                  >
                    <DrawingActionsTrigger
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={openMenuId === c.canvasId}
                      aria-label={intl.formatMessage({
                        id: 'projects.moreActions',
                      })}
                      onClick={() =>
                        setOpenMenuId((id) =>
                          id === c.canvasId ? null : c.canvasId
                        )
                      }
                    >
                      ⋯
                    </DrawingActionsTrigger>
                    {openMenuId === c.canvasId && (
                      <DrawingActionsMenu onClick={() => setOpenMenuId(null)}>
                        {renderActionButtons(c.canvasId, c.name)}
                      </DrawingActionsMenu>
                    )}
                  </CompactDrawingActions>
                ) : (
                  <DrawingActions>
                    {renderActionButtons(c.canvasId, c.name)}
                  </DrawingActions>
                )}
              </ProjectBox>
            ))}
          </ProjectList>
        </Flex>
      </Flex>
    </Project>
  );
}

export default Projects;
